import { useState, useEffect } from "react";
import { FaAngleRight } from "react-icons/fa6";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { GoPencil } from "react-icons/go";
import { BsEye } from "react-icons/bs";
import { FaRegStar } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  getAllMeals,
  getMealsByRestaurant,
  clearRestaurantMeals,
} from "../../../redux/mealManagement/mealManagementSlice";
import { getAllRestaurants } from "../../../redux/restaurants/restaurantsSlice";
import SearchableSelect from "../../../components/common/SearchableSelect";
import MealTable from "./MealTable";

// Dummy data for new tabs
interface PromotionItem {
  id: string;
  name: string;
  description: string;
  discount: number;
  restaurant: string;
  validUntil: string;
  status: 'active' | 'expired' | 'pending';
  usageCount: number;
}

interface FlaggedItem {
  id: string;
  name: string;
  restaurant: string;
  reason: string;
  reportDate: string;
  status: 'under-review' | 'resolved' | 'flagged';
  reportCount: number;
}

const dummyPromotions: PromotionItem[] = [
  {
    id: '1',
    name: 'Weekend Special - Jollof Rice',
    description: '20% off on all Jollof Rice orders',
    discount: 20,
    restaurant: 'The Place Restaurant',
    validUntil: '2024-12-31',
    status: 'active',
    usageCount: 45
  },
  {
    id: '2',
    name: 'Happy Hour - Drinks',
    description: 'Buy one get one free on all drinks',
    discount: 50,
    restaurant: 'Ocean Basket',
    validUntil: '2024-11-30',
    status: 'active',
    usageCount: 23
  },
  {
    id: '3',
    name: 'Student Discount',
    description: '15% off for students with valid ID',
    discount: 15,
    restaurant: 'KFC Ikeja',
    validUntil: '2024-10-15',
    status: 'expired',
    usageCount: 89
  }
];

const dummyFlaggedItems: FlaggedItem[] = [
  {
    id: '1',
    name: 'Chicken Burger',
    restaurant: 'Quick Bites',
    reason: 'Food quality complaint',
    reportDate: '2024-01-15',
    status: 'under-review',
    reportCount: 3
  },
  {
    id: '2',
    name: 'Vegetable Salad',
    restaurant: 'Healthy Corner',
    reason: 'Hygiene concerns',
    reportDate: '2024-01-12',
    status: 'resolved',
    reportCount: 2
  },
  {
    id: '3',
    name: 'Spicy Wings',
    restaurant: 'Wing Stop',
    reason: 'Misleading description',
    reportDate: '2024-01-10',
    status: 'flagged',
    reportCount: 5
  }
];

const Index = () => {
  const [activeTab, setActiveTab] = useState("all-meals");
  const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    meals,
    mealsStatus,
    mealsPagination,
    restaurantMeals,
    restaurantMealsStatus,
    currentRestaurantId,
  } = useAppSelector((state: RootState) => state.mealManagement);

  const { restaurants, status: restaurantsStatus } = useAppSelector((state: RootState) => state.restaurants);

  // Transform restaurants data for SearchableSelect
  const restaurantOptions = restaurants.map(restaurant => ({
    id: restaurant.id,
    name: restaurant.name
  }));

  // Fetch initial data
  useEffect(() => {
    dispatch(getAllMeals({ page: 1, limit: 20 }));
    dispatch(getAllRestaurants());
  }, [dispatch]);

  // Calculate dashboard metrics from real data
  const dashboardMetrics = [
    {
      title: "Total Meals",
      value: mealsPagination.totalItems.toString(),
      change: "Across all restaurants",
      icon: <GoPencil className="w-5 h-5" />,
    },
    {
      title: "Available Meals",
      value: meals.filter(meal => meal.isAvailable).length.toString(),
      change: "Currently available",
      icon: <GoPencil className="w-5 h-5" />,
    },
    {
      title: "Discounted Meals",
      value: meals.filter(meal => meal.isDiscount).length.toString(),
      change: "On discount",
      icon: <GoPencil className="w-5 h-5" />,
    },
    {
      title: "Average Rating",
      value: meals.length > 0 ? 
        (meals.reduce((sum, meal) => sum + meal.rating, 0) / meals.length).toFixed(1) : "0.0",
      change: "Overall rating",
      icon: <GoPencil className="w-5 h-5" />,
    },
  ];

  const handleRestaurantSelect = (restaurantId: string) => {
    setSelectedRestaurantId(restaurantId);
    if (restaurantId) {
      dispatch(getMealsByRestaurant(restaurantId));
    } else {
      dispatch(clearRestaurantMeals());
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "all-meals") {
      setSelectedRestaurantId("");
      dispatch(clearRestaurantMeals());
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-600';
      case 'expired':
        return 'bg-gray-100 text-gray-600';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'under-review':
        return 'bg-blue-100 text-blue-600';
      case 'resolved':
        return 'bg-green-100 text-green-600';
      case 'flagged':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const renderPromotionsTable = () => (
    <div className="bg-white rounded-md">
      <table className="w-full">
        <thead className="font-normal">
          <tr className="bg-gray-100 text-left">
            <th className="p-3 font-medium text-left">Promotion</th>
            <th className="p-3 font-medium text-left">Restaurant</th>
            <th className="p-3 font-medium text-left">Discount</th>
            <th className="p-3 font-medium text-left">Valid Until</th>
            <th className="p-3 font-medium text-left">Usage Count</th>
            <th className="p-3 font-medium text-left">Status</th>
            <th className="p-3 font-medium text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {dummyPromotions.length > 0 ? (
            dummyPromotions.map((promotion) => (
              <tr key={promotion.id} className="border text-sm hover:bg-gray-50">
                <td className="p-5">
                  <div>
                    <p className="font-medium">{promotion.name}</p>
                    <p className="text-xs text-gray-500">{promotion.description}</p>
                  </div>
                </td>
                <td className="p-5">{promotion.restaurant}</td>
                <td className="p-5">
                  <span className="font-medium text-orange-600">{promotion.discount}% OFF</span>
                </td>
                <td className="p-5 text-xs text-gray-500">
                  {formatDate(promotion.validUntil)}
                </td>
                <td className="p-5">{promotion.usageCount}</td>
                <td className="p-5">
                  <span
                    className={`${getStatusColor(promotion.status)} 
                      text-center rounded-md px-2 py-1 text-xs font-medium capitalize`}
                  >
                    {promotion.status}
                  </span>
                </td>
                <td className="p-5">
                  <button className="text-secondary border border-secondary rounded-md px-3 py-1 flex items-center gap-1 text-xs hover:bg-secondary hover:text-white transition-colors">
                    <BsEye />
                    View
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center py-8 text-gray-500">
                No promotions found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderFlaggedItemsTable = () => (
    <div className="bg-white rounded-md">
      <table className="w-full">
        <thead className="font-normal">
          <tr className="bg-gray-100 text-left">
            <th className="p-3 font-medium text-left">Item</th>
            <th className="p-3 font-medium text-left">Restaurant</th>
            <th className="p-3 font-medium text-left">Reason</th>
            <th className="p-3 font-medium text-left">Report Date</th>
            <th className="p-3 font-medium text-left">Report Count</th>
            <th className="p-3 font-medium text-left">Status</th>
            <th className="p-3 font-medium text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {dummyFlaggedItems.length > 0 ? (
            dummyFlaggedItems.map((item) => (
              <tr key={item.id} className="border text-sm hover:bg-gray-50">
                <td className="p-5">
                  <p className="font-medium">{item.name}</p>
                </td>
                <td className="p-5">{item.restaurant}</td>
                <td className="p-5">
                  <p className="text-xs">{item.reason}</p>
                </td>
                <td className="p-5 text-xs text-gray-500">
                  {formatDate(item.reportDate)}
                </td>
                <td className="p-5">
                  <span className="font-medium text-red-600">{item.reportCount}</span>
                </td>
                <td className="p-5">
                  <span
                    className={`${getStatusColor(item.status)} 
                      text-center rounded-md px-2 py-1 text-xs font-medium capitalize`}
                  >
                    {item.status.replace('-', ' ')}
                  </span>
                </td>
                <td className="p-5">
                  <button className="text-secondary border border-secondary rounded-md px-3 py-1 flex items-center gap-1 text-xs hover:bg-secondary hover:text-white transition-colors">
                    <BsEye />
                    View
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center py-8 text-gray-500">
                No flagged items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <button
          className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
          onClick={() => navigate(-1)}
        >
          <MdOutlineKeyboardBackspace className="w-6 h-6" />
        </button>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            <span className="font-semibold">Meal Management</span>
          </h1>
          <p className="text-sm">
            Monitor and manage meal items across all restaurants on the platform.
          </p>
        </article>
      </div>

      {/* Dashboard Metrics */}
      <section className="bg-white px-4 py-7 my-5 flex gap-3 justify-between">
        {dashboardMetrics.map((item, index) => (
          <div key={index} className="w-[269px] border rounded-md p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm">{item.title}</h3>
              <div className="p-3 rounded-full border">
                {item.icon}
              </div>
            </div>
            <p className="text-lg font-medium">{item.value}</p>
            <p className="text-xs text-gray-500">{item.change}</p>
          </div>
        ))}
      </section>

      {/* Navigation Tabs */}
      <section className="bg-[#EEEEEE40] px-3 py-5 my-3">
        <nav className="bg-white p-5 w-fit">
          <ul className="flex gap-10 text-base font-medium">
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "all-meals" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => handleTabChange("all-meals")}
              >
                All Meals
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "restaurant-meals" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => handleTabChange("restaurant-meals")}
              >
                Restaurant Meals
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "promotions" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => handleTabChange("promotions")}
              >
                Promotions
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "flagged-items" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => handleTabChange("flagged-items")}
              >
                Flagged Items
              </button>
            </li>
          </ul>
        </nav>

        {/* Restaurant Filter for Restaurant Meals Tab */}
        {activeTab === "restaurant-meals" && (
          <div className="bg-white p-4 my-4 rounded-md">
            <div className="max-w-md">
              <SearchableSelect
                options={restaurantOptions}
                value={selectedRestaurantId}
                onChange={handleRestaurantSelect}
                placeholder="Select a restaurant"
                label="Select Restaurant"
                loading={restaurantsStatus === 'loading'}
                disabled={restaurantsStatus === 'loading'}
                className="w-full"
              />
              {restaurantOptions.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {restaurantOptions.length} restaurant{restaurantOptions.length !== 1 ? 's' : ''} available
                </p>
              )}
            </div>
          </div>
        )}

        {/* Table Section */}
        <section>
          {activeTab === "all-meals" ? (
            <MealTable
              type="all-meals"
              meals={meals}
              status={mealsStatus}
              pagination={mealsPagination}
              onPageChange={(page) => dispatch(getAllMeals({ page, limit: 20 }))}
            />
          ) : activeTab === "restaurant-meals" ? (
            selectedRestaurantId ? (
              <MealTable
                type="restaurant-meals"
                restaurantMeals={restaurantMeals}
                status={restaurantMealsStatus}
                restaurantId={selectedRestaurantId}
              />
            ) : (
              <div className="bg-white p-8 rounded-md text-center">
                <p className="text-gray-500">
                  Please select a restaurant to view its meals.
                </p>
              </div>
            )
          ) : activeTab === "promotions" ? (
            renderPromotionsTable()
          ) : activeTab === "flagged-items" ? (
            renderFlaggedItemsTable()
          ) : null}
        </section>
      </section>
    </section>
  );
};

export default Index;